import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Megaphone, Monitor, Send, Smartphone, Users2 } from 'lucide-react';
import type {
  AdminUser,
  BroadcastTarget,
  PreviewRecipientsResponse,
} from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/MultiSelect';
import { Spinner } from '@/components/ui/Spinner';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';
import { useEntitatiSilvice } from '@/features/regions/useRegions';
import { useParcelFacets } from '@/features/parcels/useParcels';
import {
  useAnnounceMobileVersion,
  useAnnounceWebVersion,
  useBroadcast,
  usePreviewRecipients,
} from '@/features/notifications/useNotifications';

type Mode = 'all' | 'roles' | 'entities' | 'raioane' | 'users';

const ROLES = ['SUPER_ADMIN', 'CONTROLOR', 'ADMINISTRATOR', 'AJUTOR_ADMINISTRATOR'] as const;
type Role = (typeof ROLES)[number];

const MODES: Mode[] = ['all', 'roles', 'entities', 'raioane', 'users'];

/** Build a validated BroadcastTarget; returns null when the mode requires a
 *  selection that's still empty (so we don't fire preview/send prematurely). */
function buildTarget(
  mode: Mode,
  roles: string[],
  entities: string[],
  raioane: string[],
  userIds: string[],
): BroadcastTarget | null {
  switch (mode) {
    case 'all':
      return { mode: 'all' };
    case 'roles':
      return roles.length ? { mode: 'roles', roles: roles as Role[] } : null;
    case 'entities':
      return entities.length ? { mode: 'entities', entities } : null;
    case 'raioane':
      return raioane.length ? { mode: 'raioane', raioane } : null;
    case 'users':
      return userIds.length ? { mode: 'users', userIds } : null;
  }
}

export function NotificationsAdminPage() {
  const { t } = useTranslation(['notifications', 'common', 'admin']);
  const pushToast = useUiStore((s) => s.pushToast);

  // Composer state.
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<Mode>('all');
  const [roles, setRoles] = useState<string[]>([]);
  const [entities, setEntities] = useState<string[]>([]);
  const [raioane, setRaioane] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);

  // Option sources.
  const entitatiQ = useEntitatiSilvice();
  const facetsQ = useParcelFacets();
  const usersQ = useQuery({
    queryKey: ['admin', 'users', 'all-for-notify'],
    // Page through instead of taking the first 500: this list is the
    // recipient picker, so a truncated fetch would silently drop people
    // from a broadcast.
    queryFn: async () => {
      const items: AdminUser[] = [];
      for (let page = 1; ; page++) {
        const res = await api
          .get('admin/users', { searchParams: { page, limit: 200 } })
          .json<{ items: AdminUser[]; pagination?: { totalPages: number } }>();
        items.push(...res.items);
        const totalPages = res.pagination?.totalPages ?? 1;
        if (page >= totalPages || res.items.length === 0 || page >= 50) break;
      }
      return { items };
    },
  });

  const roleOptions: MultiSelectOption[] = ROLES.map((r) => ({
    value: r,
    label: t(`notifications:compose.roleLabel.${r}`, { defaultValue: r.replace('_', ' ') }),
  }));
  const entityOptions: MultiSelectOption[] = useMemo(
    () =>
      (entitatiQ.data?.items ?? []).map((e) => ({
        value: e.cod,
        label: e.name ? `${e.cod} — ${e.name}` : e.cod,
      })),
    [entitatiQ.data],
  );
  const raionOptions: MultiSelectOption[] = useMemo(
    () => (facetsQ.data?.raioane ?? []).map((r) => ({ value: r, label: r })),
    [facetsQ.data],
  );
  const userOptions: MultiSelectOption[] = useMemo(
    () =>
      (usersQ.data?.items ?? []).map((u) => ({
        value: u.id,
        label: `${u.fullName || u.username} (@${u.username})`,
      })),
    [usersQ.data],
  );

  const target = buildTarget(mode, roles, entities, raioane, userIds);

  // Live recipient preview (debounced).
  const preview = usePreviewRecipients();
  const [previewData, setPreviewData] = useState<PreviewRecipientsResponse | null>(null);
  const targetKey = JSON.stringify(target);
  useEffect(() => {
    if (!target) {
      setPreviewData(null);
      return;
    }
    const handle = setTimeout(() => {
      preview.mutate(
        { target },
        {
          onSuccess: (d) => setPreviewData(d),
          onError: () => setPreviewData(null),
        },
      );
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  const recipientCount = target ? (previewData?.count ?? null) : null;

  // Send.
  const broadcast = useBroadcast();
  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !!target &&
    recipientCount !== null &&
    recipientCount > 0 &&
    !broadcast.isPending;

  const onSend = () => {
    if (!target) return;
    broadcast.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        ...(url.trim() ? { url: url.trim() } : {}),
        target,
      },
      {
        onSuccess: (res) => {
          pushToast({
            variant: 'success',
            message: t('notifications:compose.toast.sent', {
              count: res.created,
              defaultValue: '{{count}} notificări trimise',
            }),
          });
          setTitle('');
          setBody('');
          setUrl('');
          setPreviewData(null);
        },
        onError: async (e) => {
          const err = await apiError(e);
          pushToast({
            variant: 'error',
            message:
              err.code === 'VALIDATION_FAILED'
                ? t('notifications:compose.toast.empty', {
                    defaultValue: 'Ținta nu are niciun destinatar.',
                  })
                : err.message,
          });
        },
      },
    );
  };

  // Version announcements.
  const announceWeb = useAnnounceWebVersion();
  const announceMobile = useAnnounceMobileVersion();

  const onAnnounce = (kind: 'web' | 'mobile') => {
    const mut = kind === 'web' ? announceWeb : announceMobile;
    mut.mutate(undefined, {
      onSuccess: (res) => {
        pushToast({
          variant: res.announced ? 'success' : 'info',
          message: res.announced
            ? t('notifications:announce.toast.sent', {
                version: res.version,
                count: res.created,
                defaultValue: 'Versiunea {{version}} anunțată ({{count}} destinatari)',
              })
            : t('notifications:announce.toast.already', {
                version: res.version,
                defaultValue: 'Versiunea {{version}} a fost deja anunțată',
              }),
        });
      },
      onError: async (e) => {
        const err = await apiError(e);
        pushToast({ variant: 'error', message: err.message });
      },
    });
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl dark:text-slate-100">
          <Megaphone className="h-5 w-5 text-forest-600 dark:text-forest-300" />
          {t('notifications:admin.title', { defaultValue: 'Difuzare notificări' })}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('notifications:admin.subtitle', {
            defaultValue: 'Trimite un anunț către utilizatori și gestionează anunțurile de versiune.',
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('notifications:compose.title', { defaultValue: 'Compune anunț' })}
          </h2>
        </CardHeader>
        <CardBody className="space-y-1">
          <FormField
            label={t('notifications:compose.fieldTitle', { defaultValue: 'Titlu' })}
            required
          >
            <Input
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notifications:compose.fieldTitlePlaceholder', {
                defaultValue: 'Titlul anunțului',
              })}
            />
          </FormField>

          <FormField
            label={t('notifications:compose.fieldBody', { defaultValue: 'Mesaj' })}
            required
          >
            <textarea
              value={body}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-forest-400 dark:focus:ring-forest-400"
              placeholder={t('notifications:compose.fieldBodyPlaceholder', {
                defaultValue: 'Conținutul anunțului…',
              })}
            />
          </FormField>

          <FormField
            label={t('notifications:compose.fieldUrl', { defaultValue: 'Link (opțional)' })}
            hint={t('notifications:compose.fieldUrlHint', {
              defaultValue: 'Ex: /app/changelog — utilizatorul ajunge aici la click.',
            })}
          >
            <Input
              value={url}
              maxLength={500}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/app/…"
            />
          </FormField>

          {/* Targeting */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <Users2 className="h-4 w-4" />
              {t('notifications:compose.targeting', { defaultValue: 'Destinatari' })}
            </div>

            <FormField label={t('notifications:compose.mode', { defaultValue: 'Mod' })}>
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {t(`notifications:compose.modeLabel.${m}`, { defaultValue: m })}
                  </option>
                ))}
              </Select>
            </FormField>

            {mode === 'roles' ? (
              <FormField label={t('notifications:compose.roles', { defaultValue: 'Roluri' })}>
                <MultiSelect
                  options={roleOptions}
                  value={roles}
                  onChange={setRoles}
                  placeholder={t('notifications:compose.selectRoles', { defaultValue: 'Alege roluri' })}
                  countLabel={(n) =>
                    t('notifications:compose.nSelected', { count: n, defaultValue: '{{count}} selectate' })
                  }
                />
              </FormField>
            ) : null}

            {mode === 'entities' ? (
              <FormField label={t('notifications:compose.entities', { defaultValue: 'Entități silvice' })}>
                <MultiSelect
                  options={entityOptions}
                  value={entities}
                  onChange={setEntities}
                  placeholder={t('notifications:compose.selectEntities', { defaultValue: 'Alege entități' })}
                  countLabel={(n) =>
                    t('notifications:compose.nSelected', { count: n, defaultValue: '{{count}} selectate' })
                  }
                />
              </FormField>
            ) : null}

            {mode === 'raioane' ? (
              <FormField label={t('notifications:compose.raioane', { defaultValue: 'Raioane' })}>
                <MultiSelect
                  options={raionOptions}
                  value={raioane}
                  onChange={setRaioane}
                  placeholder={t('notifications:compose.selectRaioane', { defaultValue: 'Alege raioane' })}
                  countLabel={(n) =>
                    t('notifications:compose.nSelected', { count: n, defaultValue: '{{count}} selectate' })
                  }
                />
              </FormField>
            ) : null}

            {mode === 'users' ? (
              <FormField label={t('notifications:compose.users', { defaultValue: 'Utilizatori' })}>
                <MultiSelect
                  options={userOptions}
                  value={userIds}
                  onChange={setUserIds}
                  placeholder={t('notifications:compose.selectUsers', { defaultValue: 'Alege utilizatori' })}
                  countLabel={(n) =>
                    t('notifications:compose.nSelected', { count: n, defaultValue: '{{count}} selectați' })
                  }
                />
              </FormField>
            ) : null}

            {/* Live recipient count */}
            <div className="mt-2 flex items-center gap-2 text-sm">
              {preview.isPending && target ? (
                <Spinner className="h-4 w-4" />
              ) : null}
              <span className="text-slate-600 dark:text-slate-300">
                {target === null
                  ? t('notifications:compose.needSelection', {
                      defaultValue: 'Selectează cel puțin o opțiune.',
                    })
                  : t('notifications:compose.recipientCount', {
                      count: recipientCount ?? 0,
                      defaultValue: '{{count}} destinatari',
                    })}
              </span>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={onSend} disabled={!canSend} loading={broadcast.isPending}>
              <Send className="mr-1.5 h-4 w-4" />
              {t('notifications:compose.send', { defaultValue: 'Trimite anunțul' })}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Version announcements */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('notifications:announce.title', { defaultValue: 'Anunțuri de versiune' })}
          </h2>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('notifications:announce.subtitle', {
              defaultValue:
                'Notifică toți utilizatorii activi despre o versiune nouă. Reapelarea este fără efect (idempotent).',
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => onAnnounce('web')}
              loading={announceWeb.isPending}
              disabled={announceWeb.isPending}
            >
              <Monitor className="mr-1.5 h-4 w-4" />
              {t('notifications:announce.web', { defaultValue: 'Anunță versiunea web' })}
            </Button>
            <Button
              variant="secondary"
              onClick={() => onAnnounce('mobile')}
              loading={announceMobile.isPending}
              disabled={announceMobile.isPending}
            >
              <Smartphone className="mr-1.5 h-4 w-4" />
              {t('notifications:announce.mobile', { defaultValue: 'Anunță versiunea mobilă' })}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
